import React from 'react';
import styled, { css } from 'styled-components';

type FieldShellProps = {
  $hasError: boolean;
  $disabled: boolean;
  $fullWidth: boolean;
};

const fieldShellStyles = css<FieldShellProps>`
  display: flex;
  align-items: center;
  gap: var(--space-2);
  background: var(--color-surface);
  border: 1px solid
    ${(p) => (p.$hasError ? 'var(--color-danger)' : 'var(--color-border)')};
  border-radius: var(--radius-md);
  padding: 0 var(--space-3);
  transition:
    border-color var(--duration-fast) var(--ease-standard),
    box-shadow var(--duration-fast) var(--ease-standard),
    background var(--duration-fast) var(--ease-standard);
  width: ${(p) => (p.$fullWidth ? '100%' : 'auto')};

  ${(p) =>
    p.$disabled &&
    css`
      background: var(--color-surface-subtle);
      opacity: 0.7;
      cursor: not-allowed;
    `}

  &:focus-within {
    border-color: ${(p) =>
      p.$hasError ? 'var(--color-danger)' : 'var(--color-accent)'};
    box-shadow: var(--shadow-focus);
  }
`;

const InputShell = styled.div<FieldShellProps>`
  ${fieldShellStyles}
  height: 36px;
`;

const TextareaShell = styled.div<FieldShellProps>`
  ${fieldShellStyles}
  padding: var(--space-3);
  align-items: flex-start;
`;

const NativeInput = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-family: var(--font-sans);
  font-size: var(--font-size-base);
  color: var(--color-text);
  line-height: 1.4;

  &::placeholder {
    color: var(--color-text-tertiary);
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const NativeTextarea = styled.textarea`
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-family: var(--font-sans);
  font-size: var(--font-size-base);
  color: var(--color-text);
  line-height: var(--line-height-normal);
  resize: vertical;
  min-height: 80px;

  &::placeholder {
    color: var(--color-text-tertiary);
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const FieldGroup = styled.div<{ $fullWidth: boolean }>`
  display: inline-flex;
  flex-direction: column;
  gap: var(--space-1);
  width: ${(p) => (p.$fullWidth ? '100%' : 'auto')};
`;

const Label = styled.label`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-secondary);
`;

const HelperText = styled.span<{ $isError: boolean }>`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: ${(p) =>
    p.$isError ? 'var(--color-danger)' : 'var(--color-text-tertiary)'};
`;

const IconSlot = styled.span`
  display: inline-flex;
  align-items: center;
  color: var(--color-text-tertiary);
  flex-shrink: 0;
`;

let idCounter = 0;
const nextId = (prefix: string) => {
  idCounter += 1;
  return `${prefix}-${idCounter}`;
};

export type InputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'size'
> & {
  label?: string;
  helperText?: string;
  error?: string;
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
  fullWidth?: boolean;
};

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      helperText,
      error,
      iconLeft,
      iconRight,
      fullWidth = false,
      id,
      disabled,
      ...rest
    },
    ref
  ) => {
    const inputId = id ?? nextId('ds-input');
    const helperId = `${inputId}-helper`;
    const hasError = Boolean(error);
    const message = error ?? helperText;

    return (
      <FieldGroup $fullWidth={fullWidth}>
        {label && <Label htmlFor={inputId}>{label}</Label>}
        <InputShell
          $hasError={hasError}
          $disabled={Boolean(disabled)}
          $fullWidth={fullWidth}
        >
          {iconLeft && <IconSlot>{iconLeft}</IconSlot>}
          <NativeInput
            ref={ref}
            id={inputId}
            disabled={disabled}
            aria-invalid={hasError || undefined}
            aria-describedby={message ? helperId : undefined}
            {...rest}
          />
          {iconRight && <IconSlot>{iconRight}</IconSlot>}
        </InputShell>
        {message && (
          <HelperText id={helperId} $isError={hasError}>
            {message}
          </HelperText>
        )}
      </FieldGroup>
    );
  }
);

Input.displayName = 'Input';

export type TextareaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement> & {
  label?: string;
  helperText?: string;
  error?: string;
  fullWidth?: boolean;
};

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    { label, helperText, error, fullWidth = false, id, disabled, ...rest },
    ref
  ) => {
    const taId = id ?? nextId('ds-textarea');
    const helperId = `${taId}-helper`;
    const hasError = Boolean(error);
    const message = error ?? helperText;

    return (
      <FieldGroup $fullWidth={fullWidth}>
        {label && <Label htmlFor={taId}>{label}</Label>}
        <TextareaShell
          $hasError={hasError}
          $disabled={Boolean(disabled)}
          $fullWidth={fullWidth}
        >
          <NativeTextarea
            ref={ref}
            id={taId}
            disabled={disabled}
            aria-invalid={hasError || undefined}
            aria-describedby={message ? helperId : undefined}
            {...rest}
          />
        </TextareaShell>
        {message && (
          <HelperText id={helperId} $isError={hasError}>
            {message}
          </HelperText>
        )}
      </FieldGroup>
    );
  }
);

Textarea.displayName = 'Textarea';
